import React, { useState, useEffect } from 'react';
import * as apiService from '../services/api';

interface TokenExpirationWarningProps {
  onRefresh?: () => void;
}

const TokenExpirationWarning: React.FC<TokenExpirationWarningProps> = ({ onRefresh }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const checkTokenExpiration = () => {
      const expiresAt = localStorage.getItem('tokenExpiresAt');
      if (!expiresAt) return;

      const now = Date.now();
      const expirationTime = parseInt(expiresAt);
      const timeUntilExpiration = expirationTime - now;
      
      // Show warning if less than 10 minutes remaining
      const warningThreshold = 10 * 60 * 1000; // 10 minutes in milliseconds
      
      if (timeUntilExpiration <= warningThreshold && timeUntilExpiration > 0) {
        setShowWarning(true);
        const minutes = Math.floor(timeUntilExpiration / (60 * 1000));
        const seconds = Math.floor((timeUntilExpiration % (60 * 1000)) / 1000);
        setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      } else {
        setShowWarning(false);
      }
    };

    // Check immediately
    checkTokenExpiration();
    
    // Check every 30 seconds
    const interval = setInterval(checkTokenExpiration, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleRefreshToken = async () => {
    setIsRefreshing(true);
    try {
      const response = await apiService.refreshAuthToken();
      const { token, expiresAt } = response.data;
      
      localStorage.setItem('token', token);
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
      
      setShowWarning(false);
      onRefresh?.();
      
      console.log('[Token Warning] Token refreshed successfully');
    } catch (error) {
      console.error('[Token Warning] Failed to refresh token:', error);
      alert('Failed to extend your session. Please save your work and log in again.');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleDismiss = () => {
    setShowWarning(false);
    // Show again in 2 minutes
    setTimeout(() => {
      const expiresAt = localStorage.getItem('tokenExpiresAt');
      if (expiresAt) {
        const timeUntilExpiration = parseInt(expiresAt) - Date.now();
        if (timeUntilExpiration > 0 && timeUntilExpiration <= 10 * 60 * 1000) {
          setShowWarning(true);
        }
      }
    }, 2 * 60 * 1000);
  };

  if (!showWarning) return null;

  return (
    <div className="fixed top-4 right-4 z-50 bg-yellow-50 border border-yellow-200 rounded-lg shadow-lg p-4 max-w-sm">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            Session Expiring Soon
          </h3>
          <div className="mt-1 text-sm text-yellow-700">
            <p>Your session will expire in {timeRemaining}. Would you like to extend it?</p>
          </div>
          <div className="mt-3 flex space-x-2">
            <button
              onClick={handleRefreshToken}
              disabled={isRefreshing}
              className="bg-yellow-100 text-yellow-800 hover:bg-yellow-200 disabled:opacity-50 px-3 py-1 rounded text-sm font-medium transition-colors"
            >
              {isRefreshing ? 'Extending...' : 'Extend Session'}
            </button>
            <button
              onClick={handleDismiss}
              className="text-yellow-800 hover:text-yellow-900 px-3 py-1 rounded text-sm font-medium transition-colors"
            >
              Remind Later
            </button>
          </div>
        </div>
        <div className="ml-4 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="text-yellow-400 hover:text-yellow-500 transition-colors"
          >
            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TokenExpirationWarning;
